"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Terminal, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function MatrixRain() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let width = canvas.width = window.innerWidth;
    let height = canvas.height = window.innerHeight;
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$+-*/=%\"'#&_(),.;:?!\\|{}<>[]^~";
    const fontSize = 13;
    const columns = Math.floor(width / fontSize);
    const drops: number[] = new Array(columns).fill(1);
    let lastTime = performance.now();
    const dropInterval = 30;
    let accumulator = 0;
    const draw = () => {
      ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#39ff14";
      ctx.font = `${fontSize}px monospace`;
      for (let i = 0; i < drops.length; i++) {
        const text = characters.charAt(Math.floor(Math.random() * characters.length));
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > height && Math.random() > 0.975) {
          drops[i] = 0;
        }
      }
    };
    let animationId: number;
    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      accumulator += deltaTime;
      while (accumulator >= dropInterval) {
        draw();
        for (let i = 0; i < drops.length; i++) {
          drops[i]++;
        }
        accumulator -= dropInterval;
      }
      animationId = requestAnimationFrame(animate);
    };
    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      const newColumns = Math.floor(width / fontSize);
      if (newColumns > drops.length) {
        for (let i = drops.length; i < newColumns; i++) {
          drops[i] = 1;
        }
      }
    };
    window.addEventListener("resize", handleResize);
    animationId = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{
    opacity: 0.18
  }} />;
}
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    const currentRef = node;
    observer.observe(currentRef);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function StepIntoSnug() {
  const [scrollOffset, setScrollOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      setScrollOffset(window.scrollY * 0.15);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: buttonsRef,
    isVisible: areButtonsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="230011" id="system-initialization" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-background text-foreground">
      <MatrixRain />
      <div className="absolute inset-0 z-0">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-screen scale-105 md:grayscale brightness-50" poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/matrix_code_poster.png">
          <source src="/video/snug_steam_bg.mp4" type="video/mp4" />
        </video>
        
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.8)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--background)_0%,var(--background)_100%)]" />
        
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
        </div>

        <div className="absolute inset-0 pointer-events-none z-[1] opacity-[0.02] bg-[repeating-linear-gradient(0deg,rgba(0,0,0,0)_0px,rgba(0,0,0,0)_1px,rgba(255,255,255,1)_2px,rgba(255,255,255,1)_3px)] animate-scanline" style={{
        "--scanline-offset": `${scrollOffset}px`
      } as React.CSSProperties} />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center px-4 sm:px-6 md:px-8 lg:px-12 w-full max-w-6xl mx-auto">
        <h1 ref={headerRef} className={`font-default font-bold text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-none tracking-tighter mb-8 text-foreground transition-all duration-1000 delay-100 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="block mb-4">
            <img src="https://sway-systems.com/wp-content/uploads/2026/05/IMG_2026.png" alt="SWAY SYSTEMS" className="mx-auto w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg h-auto" loading="lazy" />
          </div>
          <span className="block text-xl sm:text-3xl md:text-5xl lg:text-6xl mt-2 opacity-90">PC REPAIR ELITE</span>
        </h1>

        <div ref={textRef} className={`w-full max-w-2xl mb-12 transition-all duration-1000 delay-200 transform ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-muted-foreground font-sans leading-relaxed">
            <span className="text-primary font-mono mr-2">&gt;</span>
            Professional hardware diagnostics, deep cleaning, and performance optimization. 
            Fast turnaround for Pflugerville's gaming rigs and workstations.
          </p>
        </div>

        <div ref={buttonsRef} className={`flex flex-col sm:flex-row items-center gap-6 transition-all duration-1000 delay-300 transform ${areButtonsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Link to="#fullservicecatalog">
            <Button size="lg" className="group relative rounded-lg text-base px-10 h-14 bg-primary text-primary-foreground font-default font-bold overflow-hidden transition-all shadow-md">
              <span className="relative z-10 flex items-center gap-2"><Cpu className="size-5" />VIEW SERVICES</span>
              <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
            </Button>
          </Link>
          
          <Link to="#terminal-interface">
            <Button size="lg" variant="outline" className="rounded-lg text-base px-10 h-14 bg-transparent backdrop-blur-md border-secondary/50 text-secondary hover:bg-secondary/10 hover:border-secondary font-default font-bold transition-all shadow-md">
              <span className="flex items-center gap-2"><Terminal className="size-5" />BOOK A REPAIR</span>
            </Button>
          </Link>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce text-primary/60">
        <span className="text-[10px] tracking-[0.3em] uppercase font-default font-medium">Scroll to Boot</span>
        <ChevronDown className="size-5" />
      </div>

      <style>{`
        @keyframes scan {
          0% { top: 0%; opacity: 0; }
          5% { opacity: 1; }
          95% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes scanline {
          0% { background-position: 0 0; -webkit-transform: translateY(var(--scanline-offset)); transform: translateY(var(--scanline-offset)); }
          100% { background-position: 0 100%; -webkit-transform: translateY(var(--scanline-offset)); transform: translateY(var(--scanline-offset)); }
        }
        .animate-scanline {
          -webkit-animation: scanline 8s linear infinite;
          animation: scanline 8s linear infinite;
        }
        @keyframes pulse-glow {
          0%, 100% { -webkit-filter: drop-shadow(0 0 10px rgba(34, 197, 94, 0.2)); filter: drop-shadow(0 0 10px rgba(34, 197, 94, 0.2)); }
          50% { -webkit-filter: drop-shadow(0 0 25px rgba(34, 197, 94, 0.5)); filter: drop-shadow(0 0 25px rgba(34, 197, 94, 0.5)); }
        }
        .pulse-glow {
          -webkit-animation: pulse-glow 3s ease-in-out infinite;
          animation: pulse-glow 3s ease-in-out infinite;
        }
      `}</style>
    </section>;
}